import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { MapContainer, Polyline, ScaleControl } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import ReactLeafletGoogleLayer from 'react-leaflet-google-layer';
import { mapColors, MapProps, Markers } from '../../../../types/map';
import L, {
  DivIcon,
  Icon,
  IconOptions,
  LatLng,
  LatLngExpression,
} from 'leaflet';
import MarkerWithInfoWindow from '../marker-with-info-window/MarkerWithInfoWindow';
import GestureControl from '../gesture-control/GestureControl';
import CoordinatesControl from '../coordinates-control/CoordinatesControl';
import MapDraw from '../draw/draw';
import images from '../../../../assets/images';
import { FullscreenControl } from 'react-leaflet-fullscreen';
import 'react-leaflet-fullscreen/styles.css';
import LegendsControl from '../legends-control/LegendsControl';
import LiveFilterIcon from '../live-filter-control/liveFilterIcon';
import { usePermission } from '../../../../contexts/PermissionContext';
import MeasuringToolIcon from '../live-tracking-measuring-tool-control/MeasuringToolIcon';
import VesselControlIcon from '../live-tracking-filter-control/VesselFilterIcon';
import DistanceCalculatorIcon from '../live-tracking-distance-calculator/DistanceCalculatorIcon';
import MeasuringToolControl from '../live-tracking-measuring-tool-control/MeasuringToolControl';

function MarsMap(props: Readonly<MapProps>, ref?: any) {
  const {
    center,
    markers,
    isDrawVisible,
    zoom,
    polygonConfig,
    circleConfig,
    showCircleSaveModal,
    showPolygonSaveModal,
    polylineConfig,
    legendsList,
    drawEventCallBack,
    setShowPolygonSaveModal,
    handleSaveClick,
    handleEditClick,
    setShowCircleSaveModal,
    handleDeleteClick,
    handlePolygonRemove,
    isDeleteVisible,
    isCustomIconVisible,
    closePopupOnOutsideClick,
    handleCircleDraw,
    onEditStart,
    liveFilter,
    isFilterVisible,
    portCallDuration,
    isVesselFilterVisible,
    isMeasuringToolVisible,
    isDistanceCalculatorVisible,
    onPointsSelected,
    selectedImo,
    enableVesselHighlight = false,
    fleetList = [],
    onFleetSelectionChange,
    searchedVessels = [],
    onVesselSelectionChange,
    transformOrigin = [12, 4],
  } = props;

  const { findFeature } = usePermission();
  const apiKey = 'AIzaSyAWtdEsXU1l1KMdMZEzcJiPRPehPysOTVA';
  const mapDrawRef = useRef<any>();
  const getIcon = useCallback(
    (marker: Markers): DivIcon | Icon<IconOptions> | undefined => {
      const isSelected =
        enableVesselHighlight &&
        (marker.isSelected === true || (marker as any).imo === selectedImo);
      switch (marker.iconName) {
        case 'arrowSymbol':
          return arrowSymbolIcon(
            marker.iconRotation ?? 0,
            marker.markerColor || mapColors.red,
            isSelected,
            transformOrigin
          );
        case 'arrowSymbolBlue':
          return arrowSymbolIcon(marker.iconRotation ?? 0, mapColors.blue);
        case 'aisGapStart':
          return L.icon({ iconUrl: images.aisGapStart, iconAnchor: [15, 15] });
        case 'aisGapEnd':
          return L.icon({ iconUrl: images.aisGapEnd, iconAnchor: [15, 15] });
        case 'stsStart':
          return L.icon({ iconUrl: images.stsStart, iconAnchor: [15, 15] });
        case 'stsEnd':
          return L.icon({ iconUrl: images.stsEnd, iconAnchor: [15, 15] });
        case 'anchorageStart':
          return L.icon({ iconUrl: images.departure, iconAnchor: [15, 15] });
        case 'anchorageEnd':
          return L.icon({ iconUrl: images.arrival, iconAnchor: [15, 15] });
        case 'portCallSymbol':
          return L.icon({
            iconUrl: images.portCallSymbol,
            iconAnchor: [15, 15],
          });
        case 'loiteringStart':
          return L.icon({
            iconUrl: images.loiteringStart,
            iconAnchor: [15, 15],
          });
        case 'loiteringEnd':
          return L.icon({ iconUrl: images.loiteringEnd, iconAnchor: [15, 15] });
        case 'label':
          return L.divIcon({
            html: `<div>${marker.label}</div>`,
            className: '',
            iconSize: [60, 20],
          });
        default:
          return L.icon({
            iconUrl: images.pinIconRed,
            iconSize: [40, 40],
            iconAnchor: [20, 40],
          });
      }
    },
    [selectedImo, enableVesselHighlight, transformOrigin]
  );

  const DEFAULT_COORDINATES: [number, number] = [0, 0];

  const mapElement = useRef<L.Map>(null);
  const [isMeasuringActive, setIsMeasuringActive] = useState(false);

  useEffect(() => {
    setTimeout(() => {
      mapElement?.current?.invalidateSize(true);
      if (center) mapElement?.current?.setView(center);
    }, 500);
  }, [mapElement, center]);

  useImperativeHandle(ref, () => ({
    mapDrawRef,
  }));

  const adjustDateLine = useCallback(
    (coords: LatLng[]) => {
      for (let i = 1; i < coords.length; i++) {
        const prevLng = coords[i - 1].lng;
        const currLng = coords[i].lng;

        if (Math.abs(currLng - prevLng) > 180) {
          if (currLng > prevLng) {
            coords[i].lng -= 360;
          } else {
            coords[i].lng += 360;
          }
        }
      }
      return coords;
    },
    [polylineConfig]
  );

  return (
    <div style={{ height: '100%' }}>
      <MapContainer
        id="marsMap"
        ref={mapElement}
        center={
          center ??
          (markers?.length ? markers[0]?.coordinates : DEFAULT_COORDINATES)
        }
        zoom={zoom ?? 6}
        style={{ height: '100%', width: '100%', top: '0', left: '0' }}
        maxBounds={[
          [85, -360],
          [-85, 360],
        ]}
        minZoom={2.4}
        maxZoom={21}
        attributionControl={true}
        scrollWheelZoom={false}
        closePopupOnClick={!!closePopupOnOutsideClick}
      >
        <ReactLeafletGoogleLayer
          styles={[
            {
              featureType: 'water',
              elementType: 'geometry',
              stylers: [{ color: '#67a1c4' }],
            },
          ]}
          apiKey={apiKey}
          type={'roadmap'}
        />
        {polylineConfig?.map((polyline, index) => {
          return (
            <Polyline
              key={`polyline-${index}`}
              dashArray={polyline.isDashedLine ? [4, 7] : undefined}
              positions={adjustDateLine(polyline.position as LatLng[])}
              color={polyline.strokeColor ?? 'red'}
            />
          );
        })}
        <MapDraw
          isVisible={isDrawVisible}
          isDeleteVisible={isDeleteVisible}
          polygonConfig={polygonConfig}
          circleConfig={circleConfig}
          setShowPolygonSaveModal={setShowPolygonSaveModal}
          setShowCircleSaveModal={setShowCircleSaveModal}
          showCircleSaveModal={showCircleSaveModal}
          showPolygonSaveModal={showPolygonSaveModal}
          drawEventCallBack={drawEventCallBack}
          handleEditClick={handleEditClick}
          handleSaveClick={handleSaveClick}
          handleDeleteClick={handleDeleteClick}
          handlePolygonRemove={handlePolygonRemove}
          handleCircleDraw={handleCircleDraw}
          isCustomIconVisible={isCustomIconVisible}
          onEditStart={onEditStart}
          ref={mapDrawRef}
        />
        {markers?.map((marker, index) => {
          return (
            <MarkerWithInfoWindow
              key={`marker-${index}`}
              position={marker.coordinates}
              icon={getIcon(marker)}
              infoWindowContent={marker.infoWindowContent}
            />
          );
        })}
        {isMeasuringToolVisible && (
          <MeasuringToolControl
            isMeasuringActive={isMeasuringActive}
            onToggleMeasuring={setIsMeasuringActive}
            onPointsSelected={onPointsSelected}
          />
        )}
        <FullscreenControl />
        <ScaleControl />
        <GestureControl />
        <CoordinatesControl />
        {findFeature('Ship.ViewNavigationHistory') && isFilterVisible && (
          <LiveFilterIcon
            liveFilters={liveFilter}
            portCallDuration={portCallDuration}
          />
        )}
        {legendsList?.length && <LegendsControl legendList={legendsList} />}
        {isVesselFilterVisible && (
          <VesselControlIcon
            fleetList={fleetList}
            onFleetSelectionChange={onFleetSelectionChange}
            searchedVessels={searchedVessels}
            onVesselSelectionChange={onVesselSelectionChange}
          />
        )}
        {isMeasuringToolVisible && (
          <MeasuringToolIcon onToggleMeasuring={setIsMeasuringActive} />
        )}
        {isDistanceCalculatorVisible && <DistanceCalculatorIcon />}
      </MapContainer>
    </div>
  );
}

const arrowSymbolIcon = (
  rotation: number,
  color: string,
  isSelected = false,
  transformOrigin: [number, number] = [12, 4]
) => {
  const html = isSelected
    ? `
      <div style="position: relative; width: 36px; height: 36px;">
        <svg viewBox="0 0 36 36" style="position: absolute; top: 0; left: 0; width: 36px; height: 36px;">
          <path d="M4 4 L4 12 M4 4 L12 4" stroke="red" stroke-width="3" fill="none" stroke-linecap="round"/>
          <path d="M32 4 L32 12 M32 4 L24 4" stroke="red" stroke-width="3" fill="none" stroke-linecap="round"/>
          <path d="M4 32 L4 24 M4 32 L12 32" stroke="red" stroke-width="3" fill="none" stroke-linecap="round"/>
          <path d="M32 32 L32 24 M32 32 L24 32" stroke="red" stroke-width="3" fill="none" stroke-linecap="round"/>
        </svg>
        <svg 
          viewBox="0 0 24 24" 
          fill="${color}" 
          xmlns="http://www.w3.org/2000/svg" 
          style="position: absolute; top: 6px; left: 6px; width: 24px; height: 24px; transform-origin: 12px 12px; transform: rotate(${rotation}deg);"
          stroke="${color}">
          <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
          <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round" stroke="#CCCCCC" stroke-width="0.048"></g>
          <g id="SVGRepo_iconCarrier">
            <path opacity="0.15" d="M12 3L19 20L12 17L5 20L12 3Z" fill="${color}"></path>
            <path d="M19 20L12 3L5 20L12 17L19 20Z" stroke="${color}" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
          </g>
        </svg>
      </div>
    `
    : `
    <svg 
      viewBox="0 0 24 24" 
      fill="${color}" 
      xmlns="http://www.w3.org/2000/svg" 
      style="transform-origin: ${transformOrigin[0]}px ${transformOrigin[1]}px; transform: rotate(${rotation}deg);"
      stroke="${color}">
      <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
      <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round" stroke="#CCCCCC" stroke-width="0.048"></g>
      <g id="SVGRepo_iconCarrier">
        <path opacity="0.15" d="M12 3L19 20L12 17L5 20L12 3Z" fill="${color}"></path>
        <path d="M19 20L12 3L5 20L12 17L19 20Z" stroke="${color}" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
      </g>
    </svg>`;

  return L.divIcon({
    html,
    className: '',
    iconSize: [24, 24],
    iconAnchor: transformOrigin,
  });
};
export default forwardRef(MarsMap);
